import { nonReactiveState } from './index';
import { GetterTree } from 'vuex'
import RootState from '@vue-storefront/core/types/RootState'
import CategoryState from './CategoryState'
import { compareByLabel, _prepareCategoryPathIds, getSearchOptionsFromRouteParams } from '../../helpers/categoryHelpers'
import config from 'config'
import FilterVariant from '../../types/FilterVariant'
import { optionLabel } from '@vue-storefront/core/modules/catalog/helpers'
import { safeToString, safeTrim } from '@vue-storefront/core/helpers/primitives'
import forEach from 'lodash-es/forEach'
import get from 'lodash-es/get'
import { getFiltersFromQuery } from '../../helpers/filterHelpers'
import { Category } from '../../types/Category'
import { parseCategoryPath } from '@vue-storefront/core/modules/breadcrumbs/helpers'
import { currentStoreView, removeStoreCodeFromRoute } from '@vue-storefront/core/lib/multistore'
import cloneDeep from 'lodash-es/cloneDeep'

function mapCategoryProducts (productsFromState, productsData) {
  return productsFromState.map(prodState => {
    if (typeof prodState === 'string') {
      const product = productsData.find(prodData => prodData.sku === prodState)
      return cloneDeep(product)
    }
    return prodState
  })
}

const getters: GetterTree<CategoryState, RootState> = {
  getCategories: (state): Category[] => Object.values(state.categoriesMap),
  getCategoriesMap: (state): { [id: string]: Category} => state.categoriesMap,
  getNotFoundCategoryIds: (state): string[] => state.notFoundCategoryIds,
  getCategoryProducts: (state) => mapCategoryProducts(state.products, nonReactiveState.products),
  getCategoryFrom: (state, getters) => (path = '') => {
    return getters.getCategories.find(category => (removeStoreCodeFromRoute(path) as string).replace(/^(\/)/gm, '') === category.url_path)
  },
  getCategoryByParams: (state, getters, rootState) => (params: { [key: string]: string } = {}) => {
    const searchOptions = getSearchOptionsFromRouteParams(params)
    // Without route params, every category matched (0 checks === 0 keys) and `.find` returned the first in the map — usually root/home.
    if (Object.keys(searchOptions).length === 0) {
      return undefined
    }
    return getters.getCategories.find(category => {
      const valueCheck = []
      forEach(searchOptions, (value, key) => valueCheck.push(category[key] && category[key] === (category[key].constructor)(value)))
      return valueCheck.filter(check => check === true).length === Object.keys(searchOptions).length
    })
  },
  getCurrentCategory: (state, getters, rootState) => {
    const routeState = rootState.route
    if (!routeState) {
      return {} as Category
    }
    const dispatcherParams =
      routeState.meta && (routeState.meta as { dispatcherParams?: Record<string, string> }).dispatcherParams
    const params = {
      ...(dispatcherParams || {}),
      ...(routeState.params || {})
    }
    const searchOptions = getSearchOptionsFromRouteParams(params)
    if (Object.keys(searchOptions).length > 0) {
      const byParams = getters.getCategoryByParams(params)
      if (byParams && Object.keys(byParams).length > 0) {
        return byParams
      }
    }
    if (routeState.path) {
      const fromPath = getters.getCategoryFrom(routeState.path)
      if (fromPath) {
        return fromPath
      }
    }
    return {} as Category
  },
  getAvailableFiltersFrom: (state, getters, rootState) => (aggregations) => {
    const filters: any = {}
    if (aggregations) { // populate filter aggregates
      for (const attrToFilter of config.products.defaultFilters) { // fill out the filter options
        const filterOptions: FilterVariant[] = []

        const uniqueFilterValues = new Set<string>()
        if (attrToFilter !== 'price') {
          if (aggregations['agg_terms_' + attrToFilter]) {
            let buckets = aggregations['agg_terms_' + attrToFilter].buckets
            if (aggregations['agg_terms_' + attrToFilter + '_options']) {
              buckets = buckets.concat(aggregations['agg_terms_' + attrToFilter + '_options'].buckets)
            }

            for (const option of buckets) {
              uniqueFilterValues.add(safeToString(option.key))
            }
          }

          uniqueFilterValues.forEach(key => {
            const label = optionLabel(rootState.attribute, { attributeKey: attrToFilter, optionId: key })
            if (safeTrim(label) !== '') { // is there any situation when label could be empty and we should still support it?
              filterOptions.push({
                id: key,
                label,
                type: attrToFilter
              })
            }
          });

          const getSortOrder = (opt) => {
            return rootState.attribute.list_by_code[attrToFilter].options.find((item) => {
              return item.value === opt.id
            }).sort_order;
          }
          filters[attrToFilter] = filterOptions.sort((a, b) => {
            return getSortOrder(a) - getSortOrder(b);
          });
          // filters[attrToFilter] = filterOptions.sort(compareByLabel)
        } else { // special case is range filter for prices
          const currencySign = currentStoreView().i18n.currencySign

          if (aggregations['agg_range_' + attrToFilter]) {
            let index = 0
            const count = aggregations['agg_range_' + attrToFilter].buckets.length
            for (const option of aggregations['agg_range_' + attrToFilter].buckets) {
              filterOptions.push({
                id: option.key,
                type: attrToFilter,
                from: option.from,
                to: option.to,
                label: (index === 0 || (index === count - 1)) ? (option.to ? '< ' + currencySign + option.to : '> ' + currencySign + option.from) : currencySign + option.from + (option.to ? ' - ' + option.to : ''), // TODO: add better way for formatting, extract currency sign
                single: true
              })
              index++
            }
            filters[attrToFilter] = filterOptions
          }
        }
      }
      // Add sort to available filters
      const variants = []
      Object.keys(config.products.sortByAttributes).map(label => {
        variants.push({
          label,
          id: config.products.sortByAttributes[label],
          type: 'sort'
        })
      })
      filters.sort = variants
    }
    return filters
  },
  getFiltersMap: state => state.filtersMap,
  getAvailableFilters: (state, getters) => {
    const categoryId = get(getters.getCurrentCategory, 'id', null)
    return state.filtersMap[categoryId] || {}
  },
  getCurrentFiltersFrom: (state, getters, rootState) => (filters, categoryFilters) => {
    const currentQuery = filters || rootState.route[config.products.routerFiltersSource]
    const availableFilters = categoryFilters || getters.getAvailableFilters
    return getFiltersFromQuery({ availableFilters, filtersQuery: currentQuery })
  },
  getCurrentSearchQuery: (state, getters, rootState) => getters.getCurrentFiltersFrom(rootState.route[config.products.routerFiltersSource]),
  getCurrentFilters: (state, getters) => getters.getCurrentSearchQuery.filters,
  hasActiveFilters: (state, getters) => !!Object.keys(getters.getCurrentFilters).length,
  getSystemFilterNames: () => config.products.systemFilterNames,
  getBreadcrumbs: (state, getters) => getters.getBreadcrumbsFor(getters.getCurrentCategory),
  getBreadcrumbsFor: (state, getters) => category => {
    if (!category) return []
    const categoryHierarchyIds = _prepareCategoryPathIds(category)
    const resultCategoryList = categoryHierarchyIds.map(categoryId => {
      return getters.getCategoriesMap[categoryId]
    }).filter(c => !!c)
    return parseCategoryPath(resultCategoryList)
  },
  getCategorySearchProductsStats: state => state.searchProductsStats || {},
  getCategoryProductsTotal: (state, getters) => {
    const { total } = getters.getCategorySearchProductsStats
    const totalValue = typeof total === 'object' ? total.value : total

    return totalValue || 0
  },
  getMenuCategories (state, getters, rootState, rootGetters) {
    return state.menuCategories || rootGetters['category/getCategories']
  }
}

export default getters
